import { useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { EventDialog } from "@/components/EventDialog";
import api from "@/lib/api";
import { assetUrl, fmtDate, timeRange, todayStr, canSignUp, fmtPrice } from "@/lib/events";
import { ChevronLeft, ChevronRight, MapPin } from "lucide-react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const pad = (n) => String(n).padStart(2, "0");

function MonthGrid({ year, month, byDate, onPick }) {
  const first = new Date(year, month, 1).getDay();
  const count = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(first).fill(null), ...Array.from({ length: count }, (_, i) => i + 1)];
  const today = todayStr();
  return (
    <div className="grid grid-cols-7 gap-px bg-[#dfd8da] border border-[#dfd8da] rounded-2xl overflow-hidden" data-testid="calendar-grid">
      {DAYS.map((d) => <div key={d} className="bg-[#550001] text-white text-[11px] sm:text-xs font-bold uppercase tracking-wide text-center py-2">{d}</div>)}
      {cells.map((day, i) => {
        const key = day && `${year}-${pad(month + 1)}-${pad(day)}`;
        const evs = (key && byDate[key]) || [];
        return (
          <div key={i} className={`bg-white min-h-[64px] sm:min-h-[104px] p-1 sm:p-1.5 ${!day ? "bg-[#f9f6f6]" : ""}`} data-testid={day ? `calendar-day-${key}` : undefined}>
            {day && <div className={`text-xs font-bold ${key === today ? "inline-flex w-6 h-6 items-center justify-center rounded-full bg-[#920e12] text-white" : "text-[#545454]"}`}>{day}</div>}
            <div className="mt-1 space-y-1">
              {evs.map((e) => (
                <button key={e.id} onClick={() => onPick(e)} data-testid={`calendar-event-${e.id}`}
                  className="block w-full text-left text-[10px] sm:text-xs leading-tight px-1 sm:px-1.5 py-1 rounded bg-[#ffdedf] text-[#920e12] font-semibold hover:bg-[#920e12] hover:text-white transition-colors duration-150 truncate">
                  <span className="hidden sm:inline">{e.title}</span><span className="sm:hidden" aria-hidden="true">●</span>
                  <span className="sr-only sm:hidden">{e.title}</span>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EventCard({ e, onPick }) {
  return (
    <button onClick={() => onPick(e)} data-testid={`upcoming-event-${e.id}`}
      className="group flex gap-4 w-full text-left bg-white rounded-2xl border border-[#dfd8da] p-4 hover:border-[#920e12] transition-colors duration-150">
      <div className="shrink-0 w-16 text-center rounded-xl bg-[#550001] text-white py-2">
        <div className="text-[10px] uppercase tracking-widest font-bold">{fmtDate(e.date, { month: "short" })}</div>
        <div className="text-2xl font-black">{Number(e.date.slice(8))}</div>
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="font-bold text-[#1a1617] group-hover:text-[#920e12]">{e.title}</h3>
        <p className="text-sm text-[#545454]">{fmtDate(e.date, { weekday: "long" })}{timeRange(e) && ` · ${timeRange(e)} ET`}</p>
        {e.location_name && <p className="text-sm text-[#545454] flex items-center gap-1"><MapPin size={13} aria-hidden="true" />{e.location_name}{e.city ? `, ${e.city}` : ""}</p>}
        {canSignUp(e) && <span className="mt-1 inline-block px-2 py-0.5 rounded-full bg-[#920e12] text-white text-xs font-bold" data-testid={`upcoming-signup-${e.id}`}>Sign up · {fmtPrice(e.price_cents)}</span>}
      </div>
      {e.image_url && <img src={assetUrl(e.image_url)} alt="" aria-hidden="true" loading="lazy" className="hidden sm:block w-28 h-20 object-cover rounded-lg" />}
    </button>
  );
}

export default function Calendar() {
  const [events, setEvents] = useState([]);
  const [picked, setPicked] = useState(null);
  const now = new Date();
  const [ym, setYm] = useState([now.getFullYear(), now.getMonth()]);

  useEffect(() => { api.get("/events").then(({ data }) => setEvents(data)).catch(() => {}); }, []);

  const byDate = useMemo(() => events.reduce((m, e) => ({ ...m, [e.date]: [...(m[e.date] || []), e] }), {}), [events]);
  const upcoming = events.filter((e) => e.date >= todayStr());
  const shift = (d) => setYm(([y, m]) => { const t = new Date(y, m + d, 1); return [t.getFullYear(), t.getMonth()]; });
  const label = new Date(ym[0], ym[1], 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return (
    <div data-testid="calendar-page">
      <PageHeader eyebrow="Calendar" title="Upcoming classes & events"
        subtitle="Sessions, pop-up classes, camps and workshops. Tap any event for details, registration, and to add it to your own calendar." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <section className="lg:col-span-2" aria-label="Month view">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-[#550001]" data-testid="calendar-month-label">{label}</h2>
            <div className="flex gap-2">
              <button onClick={() => shift(-1)} aria-label="Previous month" data-testid="calendar-prev" className="p-2 rounded-full border border-[#dfd8da] hover:bg-[#920e12] hover:text-white transition-colors duration-150"><ChevronLeft size={18} /></button>
              <button onClick={() => setYm([now.getFullYear(), now.getMonth()])} data-testid="calendar-today" className="px-3 rounded-full border border-[#dfd8da] text-sm font-semibold hover:bg-[#920e12] hover:text-white transition-colors duration-150">Today</button>
              <button onClick={() => shift(1)} aria-label="Next month" data-testid="calendar-next" className="p-2 rounded-full border border-[#dfd8da] hover:bg-[#920e12] hover:text-white transition-colors duration-150"><ChevronRight size={18} /></button>
            </div>
          </div>
          <MonthGrid year={ym[0]} month={ym[1]} byDate={byDate} onPick={setPicked} />
        </section>
        <section aria-label="Upcoming events">
          <h2 className="text-2xl font-bold text-[#550001] mb-4">Coming up</h2>
          <div className="space-y-3" data-testid="upcoming-list">
            {upcoming.map((e) => <EventCard key={e.id} e={e} onPick={setPicked} />)}
            {upcoming.length === 0 && <p className="text-[#545454]" data-testid="upcoming-empty">No upcoming events yet. Check back soon!</p>}
          </div>
        </section>
      </div>
      <EventDialog event={picked} onClose={() => setPicked(null)} />
    </div>
  );
}
